import { MoreVert, MoreVertTwoTone } from '@mui/icons-material';
import { Box, Button, Card, CardContent, CardMedia, Chip, Divider, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import carGreyLogo from '../../../components/assets/car-grey.svg';
import theme from '../../../theme';
import { getRGBAFromColor } from '../../../utils/methods/getCssVariables';
import { convertUtcStringFormat, formatAuctionDateTime } from '../../../utils/methods/dateTime';
import { useCountdownTimer } from '../../../customHooks/useCountdownTimer';
import { CAR_STATUS, CAR_STATUS_LABELS, CAR_STATUSES, ORDER_PAYMENT_STATUS } from '../../../utils/constants/carData';
import { getCommaFormattedNumber, getCommaFormattedPrice } from '../../../utils/methods/stringMethods';
import { auctionTypes, carApprovalStatus, localAuction } from '../../../utils/constants/auction';
import carParkingLogo from '../../../components/assets/car-parking.svg';
import { useSelector } from 'react-redux';
import { reducerPaths } from '../../../app/reducerPaths';
import checkmarkCircle from '../../../components/assets/checkmark-circle.svg';
import '../../../i18n';
import { isVehicleEditButtonEnabled, isVehicleSold } from '../../../utils/methods/generalVehicleMethods';
import { carContainerTypes } from '../../../utils/constants/ui/carContainers';
import { USER_ROLES } from '../../../utils/constants/userRoles';
import { routes } from '../../../utils/constants/routes';
import { useLocation } from 'react-router-dom';
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BoltIcon from "@mui/icons-material/Bolt";
import { DrivingConditionReverseMap } from '../../../utils/constants/sellMyCar';
import CurrencyTag from '../../../components/ui/Currency/CurrencyTag';
import locationIcon from "../../../assets/location.jpeg";
import dayjs from 'dayjs';
import { currencies } from '../../../utils/constants/currencies';


const BuyNowChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  borderRadius: '0.7rem',
  zIndex: 1,
}));

// Updated AuctionInfoBox with better positioning and spacing
const AuctionInfoBox = styled(Box)(({ theme }) => ({
  // position: "absolute",
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.5, 1),
  borderRadius: '0.5rem',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  zIndex: 1,
  border: `1px solid ${theme.palette.text.secondary}`,
  textAlign: 'right',
}));

// Fixed RemainingTimeBox - positioned at top-right
const RemainingTimeBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '0.5rem',
  right: '0.5rem',
  border: `1px solid ${theme.palette.text.secondary}`,
  backgroundColor: getRGBAFromColor({
    color: theme.palette.text.primary,
    alpha: '0.35',
  }),
  color: theme.palette.common.white,
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(0.5),
  fontSize: '0.9rem',
  fontWeight: 'bold',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
}));

const TimeSegment = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginRight: '0.5rem',
});

const TimeValue = styled(Typography)(({ theme }) => ({
  width: '100%',
  color: theme.palette.text.secondary,
  textAlign: 'left',
  fontWeight: 'bold',
}));

const TimeUnit = styled(Typography)(({ theme }) => ({
  width: '100%',
  color: theme.palette.text.secondary,
  textAlign: 'left',
  fontSize: '0.7rem',
}));

const Separator = styled(Box)({
  width: '1px',
  backgroundColor: 'rgba(255, 255, 255, 0.5)', // Semi-transparent white for fading effect
  marginRight: '0.5rem',
  marginLeft: '0.5rem',
  alignSelf: 'center', // Vertically center the separator
  height: '1.2rem', // Adjust height as needed
  backgroundImage:
    'linear-gradient(to top, transparent 0%, rgba(255, 255, 255, 0.5) 20%, rgba(255, 255, 255, 0.5) 80%, transparent 100%)', // Fade effect
});

// Updated LiveAuctionTimeBox for better positioning
const LiveAuctionTimeBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '0.5rem',
  right: '0.5rem',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: theme.palette.common.white,
  padding: '0.5rem 1rem',
  borderRadius: '0.5rem',
  fontSize: '0.8rem',
  fontWeight: '600',
  zIndex: 2,
  backdropFilter: 'blur(4px)',
}));

const getStatusBgColor = (status) => {
  switch (status) {
    case CAR_STATUS.DRAFT:
      return 'primary.orange';
    case CAR_STATUS.DISAPPROVED:
      return 'primary.red';
    case CAR_STATUS.RELIST_PENDING:
      return 'primary.orange';
    case CAR_STATUS.LIST_TO_AUCTION:
      return 'primary.orange';
    case CAR_STATUS.UNSOLD_CAR:
      return 'primary.red';
    case CAR_STATUS.CANCELLED:
      return 'primary.red';
    case CAR_STATUS.SOLD_CAR:
      return 'primary.green';
    case CAR_STATUS.APPROVAL_PENDING:
      return 'primary.orange';
    case CAR_STATUS.LISTED_TO_AUCTION:
      return 'primary.main';
    case CAR_STATUS.LIVE_IN_AUCTION:
      return 'primary.main';
    case CAR_STATUS.IN_NEGOTIATION:
      return 'primary.main';
    default:
      return 'primary.main';
  }
};

const getOrderStatusBgColor = (status) => {
  console.log(status, status === ORDER_PAYMENT_STATUS.SUCCESSFUL, "getOrderStatusBgColor")
  switch (status) {
    case ORDER_PAYMENT_STATUS.FAILED:
      return 'primary.red';
    case ORDER_PAYMENT_STATUS.SUCCESS:
      return 'primary.green';
    case ORDER_PAYMENT_STATUS.PENDING:
      return 'primary.orange';
    default:
      return 'primary.main';
  }
};

const getPaymentStatusBgColor = (status) => {
  switch (status) {
    case ORDER_PAYMENT_STATUS.PENDING:
      return 'primary.yellow';
    case ORDER_PAYMENT_STATUS.SUCCESSFUL:
      return 'primary.green';
    case ORDER_PAYMENT_STATUS.FAILED:
      return 'primary.red';
  }
};

export const VehicleListingCard = ({
  showRemainingTime = false,
  isGarageCar = false,
  status = null,
  car,
  onEdit,
  onEditCb,
  showEdit,
  showRelist,
  onRelist,
  onClick,
  timerRemainingSeconds,
  showCancelAuction,
  onCancelAuction,
  onClickShowSimilarCars,
  showCarLabelStatus = true,
  cardType,
  tab,
  ...otherProps
}) => {
  const { t } = useTranslation("liveAuction"); 

  const maxWidth1850px = useMediaQuery('(max-width: 1850px)')
  const isTablet = useMediaQuery('(min-width: 1700px) and (max-width: 1850px)');
  const isMobile1 = useMediaQuery('(max-width: 1700px)');

  const auth = useSelector(store => store[reducerPaths.auth]);
  const currency = useSelector(store => store[reducerPaths.currencySlice]);

  const { countDown, isInitialised } = useCountdownTimer({
    seconds: timerRemainingSeconds || 0,
  });

  const buttonsMinWidthInPx = "250";

  console.log(auth, 'VehicleListingCard');

  return (
    <Box
      sx={{
        flexWrap: "wrap",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        border: "0.1rem solid #ccc",
        borderRadius: "0.8rem",
        boxShadow: "0 0.2rem 0.5rem rgba(0,0,0,0.1)",
        width: "100%",
        height: "auto",
        mx: "auto",
        backgroundColor: "#fff",
      }}
      onClick={() => {
        if (onClick) {
          onClick(car);
        }
      }}
    // {...otherProps}
    >
      {/* Left Image */}
      <Box
        sx={{
          position: "relative",
          maxWidth: isMobile1 ? "none" : "15rem",
          width: "20%",
          height: "100%"
          // maxHeight: "20rem"
        }}
      >
        <img
          src={car?.image || carGreyLogo}
          alt={`${car?.make} ${car?.model}`}
          style={{
            // position: 'absolute',
            top: '50%',
            left: '50%',
            // transform: `translate(-50%, -50%)`,
            width: "100%",
            height: "100%",
            borderRadius: "0.8rem",
            // height: "13rem",
            // width: car?.image ? '100%' : 'auto',
            objectFit: 'cover',
          }}
        />

        {!car?.dontShowCountDown && isInitialised ? <Chip
          label={`${countDown?.hours === 0 && countDown?.minutes === 0 && countDown?.seconds === 0 && isInitialised ? t(`Time out`): t(`hrs, mins left`, { hours: countDown?.hours, minutes: countDown?.minutes })}`}
          color="primary"
          size="small"
          sx={{ fontSize: "1.25rem", fontWeight: "500", position: "absolute", top: 10, left: 10, background: theme.palette.primary.main }}
        /> : null}

        {car?.isInUpcomingCars || car?.showCarStatusLabel ? <Chip
          label={
            <Typography color={'text.secondary'}>
              {t(isVehicleSold({ car }) ? CAR_STATUS_LABELS['sold-car'] : CAR_STATUS_LABELS[car?.car_label_status])}
            </Typography>
          }
          sx={{
            position: "absolute",
            bottom: "0.5rem",
            right: "0.5rem",
            backgroundColor: getStatusBgColor(isVehicleSold({ car }) ? CAR_STATUS.SOLD_CAR : car?.car_label_status),
            border: '1px solid',
            borderColor: 'border.white',
            height: 24,
          }}
        /> : null}
      </Box>

      {/* Right Content */}
      <Box
        sx={{
          width: isMobile1 ? "55" : "65%",
          display: "flex",
          flexDirection: isMobile1 ? "column" : "row",
          flex: "1",
          justifyContent: "space-between",
          // flex: "1 1 auto",
          p: "1.5rem",
          gap: "0.8rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
          }}
        >
          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              textDecoration: "underline",
              fontWeight: 700,
              fontSize: "1.75rem",
              color: theme.palette.text.primary,
            }}
          >
            {car?.Year || "2020"} {car?.Make || "Honda"} {car?.Model || "Civic XL 2020"}
          </Typography>

          {/* Car Details */}
          <Box 
            sx={{
              width: "100%",
              // marginLeft: "1rem",
              marginTop: "0.5rem",
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile1 ? "0.35rem" : "1rem"
            }}
          >
            <Box
              sx={{
                width: maxWidth1850px ? "33.5%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("Year:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>{car?.Year || t("N/A")}</Typography>
            </Box>
            <Box
              sx={{
                width: maxWidth1850px ? "32%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("Make:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>{car?.Make || t("N/A")}</Typography>
            </Box>
            <Box
              sx={{
                width: maxWidth1850px ? "32%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("Model:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>{car?.Model || t("N/A")}</Typography>
            </Box>
            <Box
              sx={{
                width: maxWidth1850px ? "33.5%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("Mileage:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>{car?.Mileage ? `${getCommaFormattedNumber(car?.Mileage)} ${car?.Mileage_Unit}` : t("N/A")}</Typography>
            </Box>
            <Box
              sx={{
                width: maxWidth1850px ? "32%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("Trim:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>{car?.Trim || t("N/A")}</Typography>
            </Box>
            <Box
              sx={{
                width: maxWidth1850px ? "32%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("Location:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>{car?.Location || t("N/A")}</Typography>
            </Box>
            <Box
              sx={{
                width: maxWidth1850px ? "33.5%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("VIN:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>
                {car?.VIN || t("N/A")}
              </Typography>
            </Box>
            <Box
              sx={{
                width: maxWidth1850px ? "32%" : "23%"
              }}
            >
              <Typography sx={{ fontSize: "1rem", fontWeight: 300, color: "text.primary" }}>
                {t("Color:")}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 400 }}>
                {car?.Color || t("N/A")}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: "1rem" }} />
        </Box>

        {/* Footer Section */}
        <Box
          sx={{
            minWidth: `${buttonsMinWidthInPx}px`,
            width: isMobile1 ? "100%" : "15%",
            display: "flex",
            flexDirection: isMobile1 ? "row" : "column",
            justifyContent: isMobile1 ? "flex-start" : "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          {/* Left - Current Bid */}
          {!car?.dontShowCurrentOffer && (car?.Highest_Bid_Amount || car?.highest_bid) && !isVehicleSold({ car }) ? <Box
            sx={{
              display: "flex",
              flexDirection: isMobile1 ? "row" : "column",
              alignItems: "center",
              gap: isMobile1 ? "0.5rem" : "0",
              my: "auto"
            }}
          >
            <Typography sx={{ fontSize: "1.5rem", textAlign: "center", color: "text.primary" }}>
              {t("Current Offer:")}
            </Typography>
            <Typography sx={{ fontWeight: 600, textAlign: "center", fontSize: "1.25rem" }}>
              {`$${getCommaFormattedNumber(car?.Highest_Bid_Amount || car?.highest_bid)}`}
            </Typography>
          </Box> : null}

          {/* Right - Buttons */}
          <Box sx={{
            display: "flex",
            flexDirection: isMobile1 ? "row" : "column",
            gap: isMobile1 ? "1.5rem" : "0"
          }}>
            {!car?.dontShowMakeOffer && auth?.user_id !== car?.Seller_Id && !isVehicleSold({ car }) ? <Button
              variant="contained"
              sx={{
                textTransform: "none",
                fontSize: "1.25rem",
                fontWeight: "500",
                px: "1.5rem",
                py: "0.6rem",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              onClick={() => {
                if (onClick) {
                  onClick(car);
                }
              }}
            >
              {t("Make Offer")}
            </Button> : null}

            {car?.navigateToDetailsBtnLabel && car?.showNavigateToDetailsPage ? <Button
              variant="contained"
              sx={{
                textTransform: "none",
                fontSize: "1.25rem",
                fontWeight: "500",
                px: "1.5rem",
                py: "0.6rem",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              onClick={() => {
                if (onClick) {
                  onClick(car);
                }
              }}
            >
              {t(car?.navigateToDetailsBtnLabel)}
            </Button> : null}

            {!car?.dontShowOrSeparator && auth?.user_id !== car?.Seller_Id && !isVehicleSold({ car }) ? <Box
              sx={{
                display: "flex", // Enables layout for the line and text
                alignItems: "center", // Vertically centers the "OR" text with the lines
                margin: "0.5rem 0",
              }}
            >
              {/* Horizontal Line (Left) */}
              <Box
                sx={{
                  flexGrow: 1, // Makes the line fill available space
                  borderTop: "1px solid #ccc", // The line itself
                  marginRight: "0.5rem", // Space between line and text
                }}
              />

              {/* OR Typography */}
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "0.9rem",
                  color: "#666", // Optional: slightly muted color for the text
                }}
              >
                {t("OR")}
              </Typography>

              {/* Horizontal Line (Right) */}
              <Box
                sx={{
                  flexGrow: 1, // Makes the line fill available space
                  borderTop: "1px solid #ccc", // The line itself
                  marginLeft: "0.5rem", // Space between line and text
                }}
              />
            </Box> : null}

            {!car?.dontShowBuyNow && auth?.user_id !== car?.Seller_Id && !isVehicleSold({ car }) ? <Button
              variant="outlined"
              sx={{
                width: "fit-content",
                textDecoration: "underline",
                textTransform: "none",
                fontSize: "1.25rem",
                fontWeight: "500",
                px: "1.5rem",
                py: "0.6rem",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.dark,
                },
              }}
              onClick={(e) => {
                if (otherProps?.onBuyNowClick) {
                  e?.stopPropagation?.();
                  otherProps?.onBuyNowClick(car);
                }
              }}
            >
              {t("Buy Now @", { price: getCommaFormattedNumber(car?.Reserve_Price) })}
            </Button> : null}

            {!car?.dontShowBuyNow && auth?.user_role === USER_ROLES.buyer && !isVehicleSold({ car }) ? (
              !currency?.conversionRates || !auth?.userData?.preferred_currency ? (
                <Typography
                  sx={{
                    textAlign: "center",
                    mt: "0.5rem"
                  }}
                >
                  {t("Fetching conversion rates...")}
                </Typography>
              ) : (
                <CurrencyTag
                  amount={parseInt(car?.Reserve_Price)}
                  conversionRates={currency?.conversionRates}
                  component={"span"}
                  outputCurrency={auth?.userData?.preferred_currency?.toUpperCase?.()}
                  defaultCurrency={auth?.userData?.preferred_currency?.toUpperCase?.()}
                  inputCurrency={"CAD"}
                  sx={{
                    fontSize: "1.15rem",
                    textAlign: "center",
                    mt: "0.5rem"
                  }}
                />
              )
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const CarListingCard = ({
  showRemainingTime = false,
  isGarageCar = false,
  status = null,
  car,
  onEdit,
  onEditCb,
  showEdit,
  showRelist,
  onRelist,
  onClick,
  timerRemainingSeconds,
  showCancelAuction,
  onCancelAuction,
  onClickShowSimilarCars,
  showCarLabelStatus = true,
  cardType,
  tab,
  ...otherProps
}) => {
  const { t } = useTranslation("liveAuction"); // Added hook for t()
  const auth = useSelector(store => store[reducerPaths.auth]);
  const currency = useSelector(store => store[reducerPaths.currencySlice]);

  const maxWidth1150px = useMediaQuery('(max-width: 1150px)');

  // useState variables start...
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  // useState variables end...

  // custom hooks start...
  const { countDown, isInitialised } = useCountdownTimer({
    seconds: timerRemainingSeconds || 0,
  });
  // custom hooks end...

  const showMenuButtonCondition = showCancelAuction ||
    (showEdit &&
      isVehicleEditButtonEnabled({
        car,
      })) ||
    car?.showRelist ||
    car?.car_label_status === CAR_STATUS.RELIST_PENDING ||
    car?.showEditAuction ||
    (showCancelAuction && (
      car?.car_label_status === CAR_STATUSES.approval_pending ||
      car?.car_label_status === CAR_STATUSES['listed-in-auction'] ||
      car?.car_label_status === CAR_STATUSES['listed-to-auction'] ||
      car?.car_label_status === CAR_STATUSES['list-to-auction']
    ) && (car?.Live_Auction_DateTime || car?.Buy_now_DateTime) && car?.car_label_status !== CAR_STATUSES.cancelled);

  return (
    <Card
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        borderRadius: 3,
        boxShadow: 2,
        m: "auto",
      }}
      onClick={() => {
        if (onClick) {
          onClick(car);
        }
      }}
    >
      {/* Left Image Section */}
      <Box sx={{ 
        maxHeight: "20rem",
        position: "relative", width: 300 }}>
        {/* <CardMedia
          component="img"
          image={car?.image || carGreyLogo}
          alt="Car"
          sx={{ height: "100%", objectFit: "cover" }}
        /> */}
        <img
          src={car?.image || carGreyLogo}
          alt={`${car?.make} ${car?.model}`}
          style={{
            // position: 'absolute',
            top: '50%',
            left: '50%',
            // transform: `translate(-50%, -50%)`,
            width: "100%",
            height: "100%",
            // width: car?.image ? '100%' : 'auto',
            objectFit: 'cover',
          }}
        />
        {!car?.dontShowCountDown ? <Chip
          label={countDown?.hours === 0 && countDown?.minutes === 0 && countDown?.seconds === 0 && isInitialised ? t(`Time out`) : t(`hrs, mins left`, { hours: countDown?.hours, minutes: countDown?.minutes })}
          color="primary"
          size="small"
          sx={{ fontSize: "1.25rem", fontWeight: "500", position: "absolute", top: 10, left: 10, background: theme.palette.primary.main }}
        /> : null}

        {car?.isInUpcomingCars ? <Chip
          label={
            <Typography color={'text.secondary'}>
              {t(CAR_STATUS_LABELS[car?.car_label_status])}
            </Typography>
          }
          sx={{
            position: "absolute",
            bottom: "0.5rem",
            right: "0.5rem",
            backgroundColor: getStatusBgColor(car?.car_label_status),
            border: '1px solid',
            borderColor: 'border.white',
            height: 24,
          }}
        /> : null}
      </Box>

      {/* Right Details Section */}
      <Box sx={{ 
        width: "65%",
        p: 2, display: "flex", flexDirection: "column" 
      }}>
        {/* Header Row */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight="bold"
              sx={{
                fontSize: "1.75rem",
              }}
            >
              {car?.Year} {car?.Make} {car?.Model}
            </Typography>
            <Typography variant="body2" color={theme.palette.text.primary}
              sx={{
                fontSize: "1.1rem",
                fontWeight: "500"
              }}
            >
              {`${getCommaFormattedNumber(car?.Mileage)} ${car?.Mileage_Unit} | ${car?.auction_type === localAuction ? t(`Local market`) : t(`Export market`)} | ${car?.Live_Auction_DateTime ? `${t("Live Auction")} |` : car?.Buy_now_DateTime ? `${t("Buy Now")} |` : ""} ${car?.VIN}`}
              {/* 81,225 km | AWD | LE | 7MUCAABGXPV075585 */}
            </Typography>
          </Box>
          {showMenuButtonCondition ? <Box
            sx={{
              position: "relative"
            }}
          >
            {/* <IconButton
              onClick={() => {
                setIsMenuOpen((isMenuOpen) => {
                  return !isMenuOpen;
                });
              }}
            >
              <MoreVertIcon />
            </IconButton> */}

            {isMenuOpen ? (
              <Box
                sx={{
                  borderRadius: "1rem !important",
                  display: 'flex',
                  flexDirection: 'column',
                  whiteSpace: 'nowrap',
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  background: theme.palette.text.primary,
                }}
              >

                {showEdit &&
                  isVehicleEditButtonEnabled({
                    car,
                  }) ? (
                  <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (onEdit) {
                        onEdit(car, onEditCb);
                      }
                    }}
                  >
                    {t(`Edit Vehicle`)}
                    {/* {t('edit')} */}
                  </Button>
                ) : null}

                {car?.car_label_status === CAR_STATUS.RELIST_PENDING ? (
                  <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (onEdit) {
                        onEdit(car, otherProps?.onRelistListToAuctionCb);
                      }
                    }}
                  >
                    {t(`List to auction`)}
                  </Button>
                ) : null}

                {car?.showEditAuction ? (
                  <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (car?.onEditAuction) {
                        car?.onEditAuction(car);
                      }
                    }}
                  >
                    {t(`Edit Auction`)}
                  </Button>
                ) : null}

                {showCancelAuction && (
                  car?.car_label_status === CAR_STATUSES.approval_pending ||
                  car?.car_label_status === CAR_STATUSES['listed-in-auction'] ||
                  car?.car_label_status === CAR_STATUSES['listed-to-auction'] ||
                  car?.car_label_status === CAR_STATUSES['list-to-auction']
                ) && (car?.Live_Auction_DateTime || car?.Buy_now_DateTime) && car?.car_label_status !== CAR_STATUSES.cancelled ? (
                  <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (onCancelAuction) {
                        onCancelAuction(car);
                      }
                    }}
                  >
                    {t(`Cancel auction`)}
                    {/* {t('cancelAuction')} */}
                  </Button>
                ) : null}

                {car?.showRelist ? (
                  <>
                    {<otherProps.RelistButton.component car={car} />}
                    {/* <Button
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                  onClick={() => {
                    if (onRelist) {
                      onRelist(car);
                    }
                  }}
                >
                  {t('relist')}
                </Button> */}
                  </>
                ) : null}
              </Box>
            ) : null}
          </Box> : null}
        </Box>

        {/* Buttons Row */}
        <Box  gap={1} my={1}
          sx={{
            display: "flex",
            flexDirection: maxWidth1150px ? "column" : "row"
          }}
        >
          <Button variant="outlined" size="small"
            sx={{
              fontSize: "1.25rem"
            }}
          >
            {t(DrivingConditionReverseMap[car?.check_driving_condition])}
          </Button>
          {car?.Reserve_Price ? <Button variant="outlined" size="small"
            sx={{
              fontSize: "1.25rem"
            }}
          >
            {t(`Reserve Price Set`)}
          </Button> : null}
        </Box>

        <Box>
          {car?.Location ? <Box
            sx={{
              display: "flex",
              gap: "0.25rem",
              alignItems: "center"
            }}
          >
            <Box>
              <img 
                src={locationIcon}
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  objectFit: "cover"
                }}
              /> 
            </Box>
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: "500"
              }}
            >
              {car?.Location}
            </Typography>
          </Box> : null}
        </Box>
      </Box>

      <Box
        sx={{
          width: "30%",
          marginRight: "1rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        {/* Footer */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {/* <Box>
            <Typography variant="body2" color="text.secondary">
              Within 50 km
            </Typography>
            <Typography variant="body2" color="text.secondary">
              #9 AUTO SALES
            </Typography>
          </Box> */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              marginLeft: "auto"
            }}
          >
            {!car?.isInUpcomingCars && (parseInt(car?.total_bids) >= 0 || parseInt(car?.highest_bid) >= 0) ? <Box
              sx={{
                display: "flex",
                alignSelf: maxWidth1150px ? "center" : "flex-end",
                alignItems: "center",
                flexDirection: maxWidth1150px ? "column" : "row",
                gap: "0.25rem"
              }}
            >
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                {parseInt(car?.total_bids) >= 0 ? t(`bids`, { count: car?.total_bids }) : ""}
              </Typography>
              {parseInt(car?.highest_bid) >= 0 ? <Divider
                orientation={maxWidth1150px ? "row" : "vertical"}
                flexItem
                sx={{
                  borderColor: theme.palette.text.primary,
                  width: maxWidth1150px ? "1rem" : "2px",
                  height: maxWidth1150px ? "2px" : "1rem",
                  alignSelf: "center"
                }}
              /> : null}
              {parseInt(car?.highest_bid) >= 0 ? <Typography variant="h6" fontWeight="bold">
                {parseInt(car?.highest_bid) >= 0 ? <CurrencyTag
                  outputCurrency={currencies.CAD}
                  amount={car?.highest_bid}
                  conversionRates={currency?.conversionRates}
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: '1.25rem',
                    fontWeight: "600",
                    textAlign: 'left',
                    opacity: "1"
                  }}
                  showCurrencyLabel={true}
                /> : t("No bids yet")}
              </Typography> : null}
            </Box> : null}

            {/* {car?.isInUpcomingCars ? <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                  alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                Reserve Price
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {`$${getCommaFormattedNumber(car?.Reserve_Price)}`}
              </Typography>
            </Box> : null} */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                alignSelf: car?.total_bids || parseInt(car?.highest_bid) > 0 || parseInt(car?.highest_bid) > 0 ? "flex-end" : "center"
              }}
            >
              {!car?.dontShowPlaceABid && auth?.user_id !== car?.Seller_Id ? <Button variant="contained" color="primary"
                sx={{
                  fontSize: "1.25rem",
                  padding: "0.35rem 1.5rem"
                }}
                onClick={() => {
                  if (onClick) {
                    onClick(car);
                  }
                }}
              >
                {t("Place a bid")}
              </Button> : null}

              {car?.navigateToDetailsBtnLabel && car?.showNavigateToDetailsPage ? <Button variant="contained" color="primary"
                sx={{
                  fontSize: "1.25rem"
                }}
                onClick={() => {
                  if (onClick) {
                    onClick(car);
                  }
                }}
              >
                {t(car?.navigateToDetailsBtnLabel)}
              </Button> : null}

              {/* <Button
                variant="outlined"
                startIcon={<BoltIcon color="primary" />}
                sx={{ minWidth: 80 }}
              >
                +$100
              </Button> */}
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};


export const NormalCard = ({
  showRemainingTime = false,
  isGarageCar = false,
  status = null,
  car,
  onEdit,
  onEditCb,
  showEdit,
  showRelist,
  onRelist,
  onClick,
  timerRemainingSeconds,
  showCancelAuction,
  onCancelAuction,
  onClickShowSimilarCars,
  showCarLabelStatus = true,
  cardType,
  tab,
  ...otherProps
}) => {
  const auth = useSelector((store) => store[reducerPaths.auth]);
  const { t } = useTranslation('carContainer');

  const location = useLocation();

  // useState variables start...
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  // useState variables end...

  // custom hooks start...
  const { countDown, isInitialised } = useCountdownTimer({
    seconds: timerRemainingSeconds || 0,
  });
  // custom hooks end...

  const showMenuButtonCondition =
    showCancelAuction ||
    (showEdit &&
      isVehicleEditButtonEnabled({
        car,
      })) ||
    car?.showRelist;

  return (
    <Card
      elevation={0}
      sx={{
        // minWidth: { xl: '24%', lg: '48%', md: '48%', sm: '48%', xs: '100%' }, // Fixed width from Figma
        // width: { xl: '24%', lg: '48%', md: '48%', sm: '48%', xs: '100%' }, // Fixed width from Figma
        width: { xl: '385px', lg: '305px', md: '320px', sm: '320px', xs: '100%' }, // Fixed width from Figma
        borderRadius: '16px', // Border radius from Figma
        p: '8px', // Padding from Figma
        bgcolor: 'background.card',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px', // Gap from Figma
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={() => {
        if (onClick) {
          onClick(car);
        }
      }}
    >
      {/* Car Image with Auction Timer Overlay */}
      <Box sx={{ position: 'relative', flex: '0 0 auto' }}>
        <Box position={'relative'}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '12rem',
              borderRadius: '1.4rem',
              overflow: 'hidden',
              background: theme.palette.colors.grey1,

              // position: "relative",
              // display: "flex",
              // flexDirection: "column",
              // justifyContent: "center",
              // alignItems: "center",
              // // width: "410.67px", // Image width from Figma
              // height: "240px", // Image height from Figma
              // borderRadius: "12px", // Image border radius from Figma
              // overflow: "hidden",
              // background: theme.palette.colors.grey1,
            }}
          >
            <img
              src={car.image || carGreyLogo}
              alt={`${car.make} ${car.model}`}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%)`,
                width: car?.image ? '100%' : 'auto',
                maxHeight: '12rem',
                objectFit: 'cover',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'flex-end',
                marginTop: 'auto',
                gap: '0.5rem',
                transform: 'translate(-0.5rem, -0.5rem)',
              }}
            >
              {(!tab || tab === auctionTypes.liveAuction) &&
                cardType === carContainerTypes.UPCOMING_CARS &&
                car?.Live_Auction_DateTime && (
                  <AuctionInfoBox>
                    <span
                      style={{
                        fontWeight: '500',
                        fontSize: '0.95rem',
                      }}
                    >
                      {t('liveAuctionDateTime')}
                    </span>
                    <br />
                    <span
                      style={{
                        fontWeight: '700',
                        fontSize: '1rem',
                      }}
                    >
                      {formatAuctionDateTime(car?.Live_Auction_DateTime)}
                    </span>
                  </AuctionInfoBox>
                )}

              {(!tab || tab === auctionTypes.buyNow) &&
                cardType === carContainerTypes.UPCOMING_CARS &&
                car?.Buy_now_DateTime && (
                  <AuctionInfoBox>
                    <span
                      style={{
                        fontWeight: '500',
                        fontSize: '0.95rem',
                      }}
                    >
                      {t('buyNowDateTime')}
                    </span>
                    <br />
                    <span
                      style={{
                        fontWeight: '700',
                        fontSize: '1rem',
                      }}
                    >
                      {formatAuctionDateTime(car?.Buy_now_DateTime)}
                    </span>
                  </AuctionInfoBox>
                )}
            </Box>
            {(showCarLabelStatus || car?.showCarLabelStatus) ||
              (car?.car_label_status &&
              (parseInt(car?.Seller_Id) === auth?.user_id || location?.pathname?.includes(routes.upcomingCars))) ? (
              <Box position={'absolute'} top={10} left={10}>
                <Chip
                  label={
                    <Typography color={'text.secondary'}>
                      {t(CAR_STATUS_LABELS[car?.car_label_status])}{' '}
                      {car?.car_label_status !== CAR_STATUS.DRAFT && car?.auction_type
                        ? `(${car?.auction_type === localAuction ? t('localMarket') : t('exportMarket')})`
                        : ''}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: getStatusBgColor(car?.car_label_status),
                    border: '1px solid',
                    borderColor: 'border.white',
                    height: 24,
                  }}
                />
              </Box>
            ) : null}{' '}
          </Box>
          <Box>
            {/* {(isGarageCar || car?.showStatusLabel) && car?.car_label_status && (
              <Box position={'absolute'} top={10} left={10}>
                <Chip
                  label={
                    <Typography color={'text.secondary'}>
                      {CAR_STATUS_LABELS[car?.car_label_status]}{' '}
                      {car?.car_label_status !== CAR_STATUS.DRAFT && car?.auction_type
                        ? `(${car?.auction_type === localAuction ? t('localMarket') : t('exportMarket')})`
                        : ''}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: getStatusBgColor(car?.car_label_status),
                    border: '1px solid',
                    borderColor: 'border.white',
                    height: 24,
                  }}
                />
              </Box>
            )} */}

            {car?.Carfax_Report_Url ? (
              <Box>
                <Box position={'absolute'} bottom={10} left={10}>
                  <Chip
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        <img src={checkmarkCircle} />
                        <Typography
                          color={'primary.main'}
                          sx={{
                            fontWeight: '600',
                          }}
                        >
                          {t('verified')}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '& .MuiChip-label': {
                        padding: '0',
                      },
                      borderRadius: '2rem',
                      opacity: '0.8',
                      padding: '1rem 0.75rem',
                      backgroundColor: 'white',
                      border: '2px solid',
                      borderColor: 'primary.main',
                      height: 24,
                    }}
                  />
                </Box>
              </Box>
            ) : null}

            {/* {car?.payment_status ? (
              <Box>
                <Box position={'absolute'} bottom={10} right={10}>
                  <Chip
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        <Typography
                          color={theme.palette.background.paper}
                          sx={{
                            fontWeight: '600',
                          }}
                        >
                          {car?.payment_status}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '& .MuiChip-label': {
                        padding: '0',
                      },
                      borderRadius: '2rem',
                      opacity: '0.8',
                      padding: '1rem 0.75rem',
                      backgroundColor: getPaymentStatusBgColor(car?.payment_status),
                      border: '2px solid',
                      borderColor: 'primary.main',
                      height: 24,
                    }}
                  />
                </Box>
              </Box>
            ) : null} */}
          </Box>
        </Box>
        {showRemainingTime && !isVehicleSold({ car: car }) &&
          (cardType === carContainerTypes.LIVE_AUCTION ||
            cardType === carContainerTypes.BUY_NOW ||
            cardType === carContainerTypes.WATCH_LIST) ? (
          <RemainingTimeBox>
            {countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0 ? (
              <>
                <TimeSegment>
                  <TimeValue>{`${countDown?.hours < 10 ? `0${countDown?.hours}` : countDown?.hours}`}</TimeValue>
                  <TimeUnit>{t('hours')}</TimeUnit>
                </TimeSegment>
                <Separator />
                <TimeSegment>
                  <TimeValue>{`${countDown?.minutes < 10 ? `0${countDown?.minutes}` : countDown?.minutes}`}</TimeValue>
                  <TimeUnit>{t('mins')}</TimeUnit>
                </TimeSegment>
                <Separator />
                <TimeSegment>
                  <TimeValue>{`${countDown?.seconds < 10 ? `0${countDown?.seconds}` : countDown?.seconds}`}</TimeValue>
                  <TimeUnit>{t('secs')}</TimeUnit>
                </TimeSegment>
              </>
            ) : !isInitialised ? (
              <Typography sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>Loading</Typography>
            ) : (
              <Typography sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>{t('timesUp')}</Typography>
            )}
          </RemainingTimeBox>
        ) : null}
      </Box>

      <CardContent
        sx={{
          position: 'relative',
          flex: '1 1 auto',
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }} gap={2} justifyContent={'center'}>
            {/* <Typography variant="h6" color="primary" fontWeight="bold">
              {car.year} {car.make}
            </Typography>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <span>&#x2022;</span>
            </Box>
            <Typography
              sx={{
                fontWeight: '600',
                color: theme.palette.text.primary,
              }}
            >
              {car.model}
            </Typography> */}

            <Typography variant="h6" fontWeight="bold"
              sx={{
                fontSize: "1.25rem",
              }}
            >
              {car?.Year} {car?.Make} {car?.Model}
            </Typography>
          </Box>

          {showMenuButtonCondition ? (
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                background: theme.palette.text.primary,
                borderRadius: '0.5rem',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <IconButton
                sx={{
                  padding: '0.75rem 0.5rem',
                }}
                onClick={() => {
                  setIsMenuOpen((isMenuOpen) => {
                    return !isMenuOpen;
                  });
                }}
              >
                <MoreVertTwoTone
                  sx={{
                    color: theme.palette.background.paper,
                  }}
                />
              </IconButton>
              {isMenuOpen ? (
                <Box
                  sx={{
                    borderRadius: "1rem !important",
                    display: 'flex',
                    flexDirection: 'column',
                    whiteSpace: 'nowrap',
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    background: theme.palette.text.primary,
                  }}
                >

                  {showEdit &&
                    isVehicleEditButtonEnabled({
                      car,
                    }) ? (
                    <Button
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                      onClick={() => {
                        if (onEdit) {
                          onEdit(car, onEditCb);
                        }
                      }}
                    >
                      Edit Vehicle
                      {/* {t('edit')} */}
                    </Button>
                  ) : null}

                  {car?.showListToAuction ? (
                    <Button
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                      onClick={() => {
                        if (car?.onListToAuction) {
                          car?.onListToAuction(car);
                        }
                      }}
                    >
                      List to auction
                    </Button>
                  ) : null}

                  {car?.showEditAuction ? (
                    <Button
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                      onClick={() => {
                        if (car?.onEditAuction) {
                          car?.onEditAuction(car);
                        }
                      }}
                    >
                      Edit Auction
                    </Button>
                  ) : null}

                  {showCancelAuction && (
                    car?.car_label_status === CAR_STATUSES.approval_pending ||
                    car?.car_label_status === CAR_STATUSES['listed-in-auction'] ||
                    car?.car_label_status === CAR_STATUSES['listed-to-auction'] ||
                    car?.car_label_status === CAR_STATUSES['list-to-auction']
                  ) && (car?.Live_Auction_DateTime || car?.Buy_now_DateTime) && car?.car_label_status !== CAR_STATUSES.cancelled ? (
                    <Button
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                      onClick={() => {
                        if (onCancelAuction) {
                          onCancelAuction(car);
                        }
                      }}
                    >
                      {t('cancelAuction')}
                    </Button>
                  ) : null}

                  {car?.showRelist ? (
                    <>
                      {<otherProps.RelistButton.component car={car} />}
                      {/* <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (onRelist) {
                        onRelist(car);
                      }
                    }}
                  >
                    {t('relist')}
                  </Button> */}
                    </>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                marginTop: '1rem',
                color: theme.palette.text.primary,
              }}
            >
              {t('vin')} <span style={{ fontWeight: '500', fontSize: '1rem' }}>{car.vin || t('na')}</span>
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
              {t('odometer')}{' '}
              <span style={{ fontWeight: '500', fontSize: '1rem' }}>
                {getCommaFormattedNumber(car.odometer) || t('na')}{' '}
                {getCommaFormattedNumber(car.odometer) ? car?.Mileage_Unit || t('km') : ''}
              </span>
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              }}
            >
              {t('trim')} <span style={{ fontWeight: '500', fontSize: '1rem' }}>{car.Trim || t('na')}</span>
            </Typography>
            {car?.Live_Auction_DateTime || car?.Buy_now_DateTime ? <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              }}
            >
              {t("Auction")} <span style={{ fontWeight: '500', fontSize: '1rem' }}>
                {car?.Live_Auction_DateTime ? "Live Auction" : car?.Buy_now_DateTime ? "Buy Now" : ""}
              </span>
            </Typography> : null}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          ></Box>
        </Box>
      </CardContent>

      {!isVehicleSold({ car }) && cardType === carContainerTypes.BUY_NOW &&
        (auth?.user_role === USER_ROLES.buyer || auth?.user_role === USER_ROLES.dealer) ? (
        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1rem',
            justifyContent: 'center', // Center the buttons
            width: '100%', // Ensure the box takes full width
          }}
        >
          <Button
            variant="contained"
            sx={{
              flexGrow: 1, // Allows buttons to grow and fill space
              backgroundColor: '#4263EC', // Blue color from screenshot
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              textTransform: 'none', // Prevent uppercase
              '&:hover': {
                backgroundColor: '#354CAF',
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (otherProps.onBuyNowClick) {
                otherProps.onBuyNowClick(car);
              }
            }}
          >
            Buy Now - ${getCommaFormattedPrice(car?.Reserve_Price) || 'N/A'}
          </Button>
          <Button
            variant="outlined"
            sx={{
              flexGrow: 1, // Allows buttons to grow and fill space
              borderColor: '#4263EC', // Blue border
              color: '#4263EC', // Blue text
              fontWeight: 'bold',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              textTransform: 'none', // Prevent uppercase
              '&:hover': {
                backgroundColor: '#e3e7fc',
              },
            }}
          >
            Make Offer
          </Button>
        </Box>
      ) : null}

      {/* {cardType === carContainerTypes.ORDER_CARD ? <Box
        sx={{
          // padding: "1rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          gap: "0.5rem"
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          startIcon={
            <img 
              src={carParkingLogo}
            />
          }
          sx={{
            borderRadius: "0.5rem",
            borderColor: "primary.main",
            color: "primary.main",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            padding: "0.5rem",
            ":hover": {
              borderColor: "primary.main",
              backgroundColor: "background.default",
            },
          }}
          onClick={() => {
            if (onClickShowSimilarCars) {
              onClickShowSimilarCars(car)
            }
          }}
        >
          {t("viewDetails")}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          startIcon={
            <img 
              src={carParkingLogo}
            />
          }
          sx={{
            borderRadius: "0.5rem",
            borderColor: "primary.main",
            color: "text.secondary",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            padding: "0.5rem",
            background: theme.palette.primary.main,
            ":hover": {
              borderColor: "primary.main",
              backgroundColor: theme.palette.primary.main,
            },
          }}
          onClick={() => {
            if (onClickShowSimilarCars) {
              onClickShowSimilarCars(car)
            }
          }}
        >
          {t("trackVehicle")}
        </Button>
      </Box> : null} */}
    </Card>
  );
};

export const LiveAuctionCard = ({
  isUpcomingCar = false,
  car,
  showRemainingTime,
  showMenuButton = true,
  showSimilarCarsButton = false,
  isGarageCar,
  onClick,
  status = null,
  showCancelAuction,
  onCancelAuction,
  timerRemainingSeconds,
  showCarLabelStatus = true,
  showCurrentHighestOffer = false,
  currentHighestOffer,
  onClickShowSimilarCars,
  cardWidth,
  cardMinWidth,
  showAgentPendingApprovalStatus = false,
  onEdit,
  onEditCb,
  tab,
}) => {
  // useState variables start...
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  // useState variables end...

  const { countDown } = useCountdownTimer({
    seconds: timerRemainingSeconds || 0,
  });

  const { t } = useTranslation('carContainer');

  const showMenuButtonCondition = showMenuButton && showCancelAuction;

  return (
    <Card
      sx={{
        position: 'relative',
        width: cardWidth || 'auto',
        borderRadius: '1.4rem',
        p: 1,
        bgcolor: 'background.card',
        border:
          showAgentPendingApprovalStatus &&
            ((tab === auctionTypes.liveAuction && car?.Agent_Approval_Status === carApprovalStatus.pending) ||
              (tab === auctionTypes.buyNow && car?.Agent_Approval_Status_BuyNow === carApprovalStatus.pending))
            ? `2px solid ${theme.palette.primary.red}`
            : 'none',
        minWidth: cardMinWidth || '22rem',
      }}
      onClick={() => {
        if (onClick) {
          onClick(car);
        }
      }}
    >
      {/* Car Image with Overlays */}
      <Box sx={{ position: 'relative' }}>
        <Box
          position={'relative'}
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '12rem',
              borderRadius: '1.4rem',
              overflow: 'hidden',
              background: theme.palette.colors.grey1,
              // backgroundImage: `${car.image || carGreyLogo}`,
              // backgroundPosition: "center"
            }}
          >
            <img
              src={car.image || carGreyLogo}
              alt={`${car.make} ${car.model}`}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%)`,
                width: car?.image ? '100%' : 'auto',
                maxHeight: '12rem',
                objectFit: 'cover',
              }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'flex-end',
                marginTop: 'auto',
                gap: '0.5rem',
                transform: 'translate(-0.5rem, -0.5rem)',
              }}
            >
              {(!tab || tab === auctionTypes.liveAuction) && isUpcomingCar && car?.Live_Auction_DateTime && (
                <AuctionInfoBox>
                  <span
                    style={{
                      fontWeight: '500',
                      fontSize: '0.95rem',
                    }}
                  >
                    {t('liveAuctionDateTime')}
                  </span>
                  <br />
                  <span
                    style={{
                      fontWeight: '700',
                      fontSize: '1rem',
                    }}
                  >
                    {formatAuctionDateTime(car?.Live_Auction_DateTime)}
                  </span>
                </AuctionInfoBox>
              )}

              {(!tab || tab === auctionTypes.buyNow) && isUpcomingCar && car?.Buy_now_DateTime && (
                <AuctionInfoBox>
                  <span
                    style={{
                      fontWeight: '500',
                      fontSize: '0.95rem',
                    }}
                  >
                    {t('buyNowDateTime')}
                  </span>
                  <br />
                  <span
                    style={{
                      fontWeight: '700',
                      fontSize: '1rem',
                    }}
                  >
                    {formatAuctionDateTime(car?.Buy_now_DateTime)}
                  </span>
                </AuctionInfoBox>
              )}
            </Box>
          </Box>

          <Box>
            {showCarLabelStatus && car?.car_label_status ? (
              <Box position={'absolute'} top={10} left={10}>
                <Chip
                  label={
                    <Typography color={'text.secondary'}>
                      {CAR_STATUS_LABELS[car?.car_label_status]}{' '}
                      {car?.car_label_status !== CAR_STATUS.DRAFT && car?.auction_type
                        ? `(${car?.auction_type === localAuction ? t('localMarket') : t('exportMarket')})`
                        : ''}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: getStatusBgColor(car?.car_label_status),
                    border: '1px solid',
                    borderColor: 'border.white',
                    height: 24,
                  }}
                />
              </Box>
            ) : null}{' '}
            {car?.Carfax_Report_Url ? (
              <Box>
                <Box position={'absolute'} bottom={10} left={10}>
                  <Chip
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        <img src={checkmarkCircle} />
                        <Typography
                          color={'primary.main'}
                          sx={{
                            fontWeight: '600',
                          }}
                        >
                          {t('verified')}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '& .MuiChip-label': {
                        padding: '0',
                      },
                      borderRadius: '2rem',
                      opacity: '0.8',
                      padding: '1rem 0.75rem',
                      backgroundColor: 'white',
                      border: '2px solid',
                      borderColor: 'primary.main',
                      height: 24,
                    }}
                  />
                </Box>
              </Box>
            ) : null}
          </Box>

          {showCurrentHighestOffer && (
            <Box
              sx={{
                position: 'absolute',
                top: '0.5rem',
                left: '0.5rem',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                {t('currentOffer')}{' '}
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: '600',
                }}
              >
                {car?.Highest_Bid_Amount ? `$${getCommaFormattedPrice(car?.Highest_Bid_Amount)}` : t('na')}
              </Typography>
            </Box>
          )}
        </Box>
        {showRemainingTime && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '0.5rem',
              right: '0.5rem',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: theme.palette.common.white,
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            <RemainingTimeBox>
              {countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0 ? (
                <>
                  <TimeSegment>
                    <TimeValue>{`${countDown?.hours < 10 ? `0${countDown?.hours}` : countDown?.hours}`}</TimeValue>
                    <TimeUnit>{t('hours')}</TimeUnit>
                  </TimeSegment>
                  <Separator />
                  <TimeSegment>
                    <TimeValue>{`${countDown?.minutes < 10 ? `0${countDown?.minutes}` : countDown?.minutes
                      }`}</TimeValue>
                    <TimeUnit>{t('mins')}</TimeUnit>
                  </TimeSegment>
                  <Separator />
                  <TimeSegment>
                    <TimeValue>{`${countDown?.seconds < 10 ? `0${countDown?.seconds}` : countDown?.seconds
                      }`}</TimeValue>
                    <TimeUnit>{t('secs')}</TimeUnit>
                  </TimeSegment>
                </>
              ) : (
                <Typography sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>{t('timesUp')}</Typography>
              )}
            </RemainingTimeBox>
          </Box>
        )}
      </Box>

      {/* Car Details */}
      <CardContent
        sx={{
          position: 'relative',
        }}
      >
        {isGarageCar && (
          <IconButton
            sx={{
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'text.default',
              borderRadius: 2,
            }}
          >
            <MoreVert
              sx={{
                color: 'white',
              }}
            />
          </IconButton>
        )}
        <Box
          display={'flex'}
          alignItems={'center'}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              {car.year} {car.make}
            </Typography>
            <span>&#x2022;</span>
            <Typography
              sx={{
                fontWeight: '600',
                color: theme.palette.text.primary,
              }}
            >
              {car.model}
            </Typography>
          </Box>

          {showMenuButtonCondition ? (
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                background: theme.palette.text.primary,
                borderRadius: '0.5rem',
              }}
            >
              <IconButton
                sx={{
                  padding: '0.75rem 0.5rem',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setIsMenuOpen((isMenuOpen) => {
                    return !isMenuOpen;
                  });
                }}
              >
                <MoreVertTwoTone
                  sx={{
                    color: theme.palette.background.paper,
                  }}
                />
              </IconButton>
              {isMenuOpen ? (
                <Box
                  sx={{
                    borderRadius: "1rem !important",
                    display: 'inline-block' /* Makes the container shrink-wrap its content */,
                    whiteSpace: 'nowrap' /* Prevents children from wrapping, keeping them on one line */,
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    transform: `translateX(-100%)`,
                    background: theme.palette.text.primary,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {/* {isVehicleEditButtonEnabled({
                  car
                }) ? (
                  <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (onEdit) {
                        onEdit(car, onEditCb);
                      }
                    }}
                  >
                    {t('edit')}
                  </Button>
                ) : null} */}

                  {showCancelAuction && !car?.hasLiveAuctionStarted ? (
                    <Button
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                      onClick={() => {
                        if (onCancelAuction) {
                          onCancelAuction(car);
                        }
                      }}
                    >
                      {t('cancelAuction')}
                    </Button>
                  ) : null}

                  {/* {showEdit ? (
                  <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (onEdit) {
                        onEdit(car);
                      }
                    }}
                  >
                    Edit
                  </Button>
                ) : null} */}

                  {/* {showRelist ? (
                  <Button
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      if (onRelist) {
                        onRelist(car);
                      }
                    }}
                  >
                    Relist
                  </Button>
                ) : null} */}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            marginTop: '1rem',
            color: theme.palette.text.primary,
          }}
        >
          {t('vin')}: <span style={{ fontWeight: '500', fontSize: '1rem' }}>{car.vin}</span>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
          {t('odometer')}:{' '}
          <span style={{ fontWeight: '500', fontSize: '1rem' }}>
            {getCommaFormattedNumber(car.odometer) || 'N/A'}{' '}
            {getCommaFormattedNumber(car.odometer) ? car?.Mileage_Unit || t('KM') : ''}
          </span>
        </Typography>
      </CardContent>

      {showSimilarCarsButton ? (
        <Box
          sx={{
            padding: '1rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            startIcon={<img src={carParkingLogo} />}
            sx={{
              borderRadius: '0.5rem',
              borderColor: 'primary.main',
              color: 'primary.main',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              padding: '0.5rem 1.5rem',
              ':hover': {
                borderColor: 'primary.main',
                backgroundColor: 'background.default',
              },
            }}
            onClick={() => {
              if (onClickShowSimilarCars) {
                onClickShowSimilarCars(car);
              }
            }}
          >
            {t('showSimilarCars')}
          </Button>
        </Box>
      ) : null}

      {showAgentPendingApprovalStatus &&
        ((tab === auctionTypes.liveAuction && car?.Agent_Approval_Status === carApprovalStatus.pending) ||
          (tab === auctionTypes.buyNow && car?.Agent_Approval_Status_BuyNow === carApprovalStatus.pending)) ? (
        <Box
          sx={{
            width: '7rem',
            position: 'absolute',
            bottom: '1rem',
            right: '1rem',
            background: theme.palette.primary.red,
            padding: '0.5rem',
            borderRadius: '0.5rem',
          }}
        >
          <Typography
            sx={{
              textAlign: 'right',
              wordBreak: 'break-word',
              color: theme.palette.text.secondary,
              fontWeight: '500',
            }}
          >
            {t('pendingApproval')}
          </Typography>
        </Box>
      ) : null}
    </Card>
  );
};

export const OrderCard = ({
  isUpcomingCar = false,
  car,
  showRemainingTime,
  showMenuButton = true,
  showSimilarCarsButton = false,
  isGarageCar,
  onClick,
  status = null,
  showCancelAuction,
  onCancelAuction,
  onProcessPayment,
  timerRemainingSeconds,
  showCarLabelStatus = true,
  showCurrentHighestOffer = false,
  currentHighestOffer,
  onClickShowSimilarCars,
  cardWidth,
  cardMinWidth,
}) => {
  // useState variables start...
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  // useState variables end...
  const { t } = useTranslation('carContainer');

  const auth = useSelector(store => store[reducerPaths.auth]);

  const { countDown } = useCountdownTimer({
    seconds: timerRemainingSeconds || 0,
  });

  const showMenuButtonCondition = showMenuButton && (
    auth?.user_id === parseInt(car?.winner_id) && car?.payment_status === ORDER_PAYMENT_STATUS.PENDING
  );

  return (
    <Card
      sx={{
        width: { xl: '385px', lg: '305px', md: '320px', sm: '320px', xs: '100%' }, // Fixed width from Figma
        borderRadius: '16px', // Border radius from Figma
        p: '8px', // Padding from Figma
        bgcolor: 'background.card',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px', // Gap from Figma
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={() => {
        if (onClick) {
          onClick(car);
        }
      }}
    >
      {/* Car Image with Overlays */}
      <Box sx={{ position: 'relative' }}>
        <Box
          position={'relative'}
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '12rem',
              borderRadius: '1.4rem',
              overflow: 'hidden',
              background: theme.palette.colors.grey1,
              // backgroundImage: `${car.image || carGreyLogo}`,
              // backgroundPosition: "center"
            }}
          >
            <img
              src={car.image || carGreyLogo}
              alt={`${car.make} ${car.model}`}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%)`,
                width: car?.image ? '100%' : 'auto',
                maxHeight: '12rem',
                objectFit: 'cover',
              }}
            />
          </Box>
          {showCarLabelStatus && car?.car_label_status ? (
            <Box position={'absolute'} bottom={10} right={10}>
              {/* <Chip
                label={<Typography color={'text.secondary'}>{car?.payment_status}</Typography>}
                sx={{
                  backgroundColor: getOrderStatusBgColor(car?.payment_status),
                  // backgroundColor: theme.palette.primary.green,
                  border: '1px solid',
                  borderColor: 'border.white',
                  height: 24,
                }}
              /> */}

              <Chip
                label={
                  <Typography color={'text.secondary'}>
                    {CAR_STATUS_LABELS[car?.car_label_status]}{' '}
                    {car?.car_label_status !== CAR_STATUS.DRAFT && car?.auction_type
                      ? `(${car?.auction_type === localAuction ? "Local Market" : "Export Market"})`
                      : ''}
                  </Typography>
                }
                sx={{
                  backgroundColor: getStatusBgColor(car?.car_label_status),
                  border: '1px solid',
                  borderColor: 'border.white',
                  height: 24,
                }}
              />
            </Box>
          ) : null}{' '}
        </Box>
      </Box>

      {/* Car Details */}
      <CardContent
        sx={{
          position: 'relative',
        }}
      >
        {/* {isGarageCar && (
          <IconButton
            sx={{
              position: "absolute",
              top: "0.5rem",
              right: "0.5rem",
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              bgcolor: "text.default",
              borderRadius: 2,
            }}
          >
            <MoreVert
              sx={{
                color: "white",
              }}
            />
          </IconButton>
        )} */}
        <Box
          display={'flex'}
          alignItems={'center'}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              {car.year} {car.make}
            </Typography>
            <span>&#x2022;</span>
            <Typography
              sx={{
                fontWeight: '600',
                color: theme.palette.text.primary,
              }}
            >
              {car.model}
            </Typography>
          </Box>

          {showMenuButtonCondition ? (
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                background: theme.palette.text.primary,
                borderRadius: '0.5rem',
              }}
            >
              <IconButton
                sx={{
                  padding: '0.75rem 0.5rem',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setIsMenuOpen((isMenuOpen) => {
                    return !isMenuOpen;
                  });
                }}
              >
                <MoreVertTwoTone
                  sx={{
                    color: theme.palette.background.paper,
                  }}
                />
              </IconButton>
              {isMenuOpen ? (
                <Box
                  sx={{
                    borderRadius: "1rem !important",
                    display: 'inline-block',
                    whiteSpace: 'nowrap',
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    transform: `translateX(-100%)`,
                    background: theme.palette.text.primary,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {auth?.user_id === parseInt(car?.winner_id) && car?.payment_status === ORDER_PAYMENT_STATUS.PENDING ? (
                    <Button
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                      onClick={() => {
                        if (onProcessPayment) {
                          onProcessPayment(car);
                        }
                      }}
                    >
                      {t('processPayment')}
                    </Button>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            marginTop: '1rem',
            color: theme.palette.text.primary,
          }}
        >
          {t('vin')} <span style={{ fontWeight: '500', fontSize: '1rem' }}>{car.vin}</span>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
          {t('shippingStatus')} <span style={{ fontWeight: 'normal' }}>{t('inTransit')}</span>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
          {t('odometer')}{' '}
          <span style={{ fontWeight: '500', fontSize: '1rem' }}>
            {getCommaFormattedNumber(car.odometer) || 'N/A'}{' '}
            {getCommaFormattedNumber(car.odometer) ? car?.Mileage_Unit || t('KM') : ''}
          </span>
        </Typography>
      </CardContent>

      <Box
        sx={{
          // padding: "1rem",
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          gap: '0.5rem',
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          startIcon={<img src={carParkingLogo} />}
          sx={{
            borderRadius: '0.5rem',
            borderColor: 'primary.main',
            color: 'primary.main',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            padding: '0.5rem',
            ':hover': {
              borderColor: 'primary.main',
              backgroundColor: 'background.default',
            },
          }}
          onClick={() => {
            if (onClickShowSimilarCars) {
              onClickShowSimilarCars(car);
            }
          }}
        >
          {t('viewDetails')}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          startIcon={<img src={carParkingLogo} />}
          sx={{
            borderRadius: '0.5rem',
            borderColor: 'primary.main',
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            padding: '0.5rem',
            background: theme.palette.primary.main,
            ':hover': {
              borderColor: 'primary.main',
              backgroundColor: theme.palette.primary.main,
            },
          }}
          onClick={() => {
            if (onClickShowSimilarCars) {
              onClickShowSimilarCars(car);
            }
          }}
        >
          {t('trackVehicle')}
        </Button>
      </Box>
    </Card>
  );
};

const CarContainer1 = ({
  isUpcomingCar = false,
  showRemainingTime = false,
  showSimilarCarsButton,
  isGarageCar = false,
  status = null,
  car,
  Component,
  onEdit,
  onEditCb,
  showEdit,
  showRelist,
  onRelist,
  onClick,
  timerRemainingSeconds = 0,
  showMenuButton,
  onClickShowSimilarCars,
  onProcessPayment,
  cardWidth,
  cardMinWidth,
  showAgentPendingApprovalStatus,
  tab,
  cardType,
  ...otherProps
}) => {
  return (
    <Component
      car={car}
      showRemainingTime={showRemainingTime}
      isGarageCar={isGarageCar}
      isUpcomingCar={isUpcomingCar}
      onEdit={onEdit}
      onEditCb={onEditCb}
      showEdit={showEdit}
      showRelist={showRelist}
      onRelist={onRelist}
      onClick={onClick}
      status={status}
      timerRemainingSeconds={timerRemainingSeconds}
      showMenuButton={showMenuButton}
      showSimilarCarsButton={showSimilarCarsButton}
      onClickShowSimilarCars={onClickShowSimilarCars}
      cardWidth={cardWidth}
      cardMinWidth={cardMinWidth}
      showAgentPendingApprovalStatus={showAgentPendingApprovalStatus}
      tab={tab}
      onProcessPayment={onProcessPayment}
      cardType={cardType}
      {...otherProps}
    />
  );
};

export default CarContainer1;
